import { getConfig } from "core/utilities";
import "firebase/compat/auth";
import "firebase/compat/database";
import { useAppSelector } from "hooks/hook";
import useUrlParams from "hooks/useUrlParams";
import { useEffect } from "react";
import useDimensions from "react-cool-dimensions";
import { shallowEqual, useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import { roles } from "resbutler-utils";
import { canAccessModule } from "resbutler-utils/utils/accessUtils";
import { initUI } from "resbutler-ui/init";
import useLabels from "../../hooks/useLabels";
import useMenus from "../../hooks/useMenus";
import useOperators from "../../hooks/useOperators";
import useProductAmmendments from "../../hooks/useProductAmmendments";
import useRestaurants from "../../hooks/useRestaurants";
import useUser from "../../hooks/useUser";
import { setOMSWidth } from "../../store/reducers/rootSlice";
import Offsidebar from "./Offsidebar";
import OmsHeader from "./OmsHeader";
import OmsSelector from "./OmsSelector";
import useRefreshToken from "resbutler-ui/hooks/useRefreshToken";

const Base = (props) => {
  const { restaurants, user } = useAppSelector((state) => {
    return {
      restaurants: state.root.restaurants,
      user: state.root.user,
    };
  }, shallowEqual);

  const claims = user?.customClaims;
  const isSystem = roles.hasRole(claims?.roles ?? 0, roles.roles.SYSTEM) !== 0;
  const canUseOms = isSystem || canAccessModule(claims?.grants, "pd", "oms");
  const [loading] = useUser();
  const [loadingMenus] = useMenus(loading);
  useProductAmmendments(loading);
  useRestaurants(loading);
  useOperators(loading);
  useLabels(loading);
  initUI(getConfig());
  useRefreshToken();
  const dispatch = useDispatch();
  const { observe, width: omsWidth } = useDimensions();
  const [restaurantId] = useUrlParams();

  useEffect(() => {
    if (restaurants?.[restaurantId]) window.document.title = `OMS - ${restaurants[restaurantId].name}`;
    else window.document.title = "OMS";
  }, [restaurants, restaurantId]);

  useEffect(() => {
    dispatch(setOMSWidth(Math.floor(omsWidth)));
  }, [omsWidth]);

  if (loading || loadingMenus) {
    return (
      <div className="ru-app-loader">
        <div className="ru-app-loader-brand">
          <span className="ru-app-loader-mark">
            <img src="img/WizButler-Logo-42px-sm.png" alt="WizButler" />
          </span>
          <div className="ru-app-loader-word">
            <span className="ru-app-loader-app">Operations</span>
            <span className="ru-app-loader-sub">WizButler Suite</span>
          </div>
        </div>
        <span className="ru-app-loader-spinner" aria-hidden />
        <span className="ru-app-loader-label">Loading your workspace…</span>
      </div>
    );
  }
  return (
    <div className="wrapper oms">
      <OmsHeader {...props} />
      {canUseOms ? (
        <>
          <div ref={observe} style={{ margin: "0 10px" }}>
            <OmsSelector {...props} />
          </div>
          <section className="section-container bg-dark" style={{ marginTop: "-10px", marginLeft: "10px", marginRight: "10px" }}>
            {props.children}
          </section>
        </>
      ) : (
        <section className="section-container d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
          <div className="text-center text-muted">
            <h4 className="mb-2">No OMS access</h4>
            <div>You don&apos;t have access to the Order Management System for this restaurant. Switch to a restaurant you manage, or ask an administrator to grant OMS access.</div>
          </div>
        </section>
      )}
      <Offsidebar />
      <ToastContainer autoClose={3000} pauseOnFocusLoss={false} position="bottom-right" />
    </div>
  );
};

export default Base;
